/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

let tenant = '';
let clientId = '';
let redirectUri = '';
if (process.env.NODE_ENV === 'production') {
  // Update these values based on branch
  tenant = 'bnsf';
  clientId = 'dba65104-2843-4b20-8600-2dd0bdb78392';
  redirectUri = 'https://bnsf.railpermitting.com/';
}

if (process.env.NODE_ENV === 'development') {
  tenant = 'local';
  clientId = '9a663755-67db-4c9e-9d72-a18633f51767';
  redirectUri = 'http://localhost:3000';
}

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: 'B2C_1A_SIGNUP_SIGNIN',
    editProfile: 'B2C_1A_PROFILEEDIT',
    changeSigninName: 'B2C_1A_CHANGESIGNINNAME',
    changePassword: 'B2C_1A_PASSWORDCHANGE',
    linkAccount: 'B2C_1A_ACCOUNTLINK',
    unlinkAccount: 'B2C_1A_AccountLink_Remove',
  },
  authorities: {
    signUpSignIn: {
      authority: `https://railpermitting${tenant}.b2clogin.com/railpermitting${tenant}.onmicrosoft.com/B2C_1A_SIGNUP_SIGNIN`,
    },
    editProfile: {
      authority: `https://railpermitting${tenant}.b2clogin.com/railpermitting${tenant}.onmicrosoft.com/B2C_1A_PROFILEEDIT`,
    },
    changeSigninName: {
      authority: `https://railpermitting${tenant}.b2clogin.com/railpermitting${tenant}.onmicrosoft.com/B2C_1A_CHANGESIGNINNAME`,
    },
    changePassword: {
      authority: `https://railpermitting${tenant}.b2clogin.com/railpermitting${tenant}.onmicrosoft.com/B2C_1A_PASSWORDCHANGE`,
    },
    linkAccount: {
      authority: `https://railpermitting${tenant}.b2clogin.com/railpermitting${tenant}.onmicrosoft.com/B2C_1A_ACCOUNTLINK`,
    },
    unlinkAccount: {
      authority: `https://railpermitting${tenant}.b2clogin.com/railpermitting${tenant}.onmicrosoft.com/B2C_1A_AccountLink_Remove`,
    },
  },
  authorityDomain: `railpermitting${tenant}.b2clogin.com`,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: clientId, // The SPA application registered in the B2C tenant
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: redirectUri, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    allowNativeBroker: false,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
const loginHint = localStorage.getItem('railpermitting_loginHint');
export const loginRequest = {
  scopes: [`https://railpermitting${tenant}.onmicrosoft.com/rp-api/access_as_user`],
  loginHint: loginHint ? loginHint : undefined,
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
  scopes: ['openid', 'profile'],
  loginHint: 'example@domain.net',
};
